import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  ViewContainerRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';

import { RouteSegment } from '@core/enums/route-segment.enum';

import { DropdownMenuDropdownComponent } from '@shared/components/dropdown-menu-dropdown/dropdown-menu-dropdown.component';
import { DropdownMenuOptionComponent } from '@shared/components/dropdown-menu-option/dropdown-menu-option.component';
import { PopoverDirective } from '@shared/modules/popover';

import { appDotDotDot32Icon } from '@icons/dot-dot-dot-32';
import { appExitV240Icon } from '@icons/exit-v2-40';
import { appLogout40Icon, appProfile40Icon, appProfileV240Icon } from '@icons/index';
import { appSearch20Icon } from '@icons/search-20';

import { HeaderProfileSectionViewModel } from './header-profile-section.view-model';

@Component({
  selector: 'app-header-profile-section',
  imports: [SvgIconComponent, TranslatePipe, PopoverDirective, DropdownMenuDropdownComponent, DropdownMenuOptionComponent],
  templateUrl: './header-profile-section.component.html',
  host: {
    class: 'flex justify-end items-center gap-2 lg:gap-6 text-header-actionitem-fg',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderProfileSectionComponent {
  readonly #router = inject(Router);
  readonly #overlay = inject(Overlay);
  readonly #viewContainerRef = inject(ViewContainerRef);
  readonly #destroyRef = inject(DestroyRef);
  #mobileMenuOverlayRef?: OverlayRef;

  public vm = input.required<HeaderProfileSectionViewModel>();
  public quickNavigationClick = output<void>();
  public logoutClick = output<void>();
  public exitImpersonationClick = output<void>();

  public readonly RouteSegment = RouteSegment;
  public readonly isDesktopMenuOpen = signal(false);
  public readonly isMobileMenuOpen = signal(false);
  public readonly mobileMenuTpl = viewChild.required<TemplateRef<unknown>>('mobileMenuTpl');

  constructor() {
    injectRegisterIcons([appProfile40Icon, appProfileV240Icon, appDotDotDot32Icon, appLogout40Icon, appExitV240Icon, appSearch20Icon]);
    this.#destroyRef.onDestroy(() => this.#mobileMenuOverlayRef?.dispose());
  }

  public onQuickNavigationClick(): void {
    this.quickNavigationClick.emit();
  }

  public toggleDesktopMenu(): void {
    this.isDesktopMenuOpen.update((open) => !open);
  }

  public closeDesktopMenu(): void {
    this.isDesktopMenuOpen.set(false);
  }

  public toggleMobileMenu(): void {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
      return;
    }

    this.#openMobileMenu();
  }

  public closeMobileMenu(): void {
    this.#mobileMenuOverlayRef?.detach();
    this.isMobileMenuOpen.set(false);
  }

  public onPreferencesClick(): void {
    this.closeDesktopMenu();
    this.closeMobileMenu();
    void this.#router.navigate(['/', RouteSegment.Preferences]);
  }

  public onLogoutClick(): void {
    this.closeMobileMenu();
    this.logoutClick.emit();
  }

  public onExitImpersonationClick(): void {
    this.closeDesktopMenu();
    this.closeMobileMenu();
    this.exitImpersonationClick.emit();
  }

  #openMobileMenu(): void {
    this.#mobileMenuOverlayRef ??= this.#createMobileMenuOverlay();

    if (!this.#mobileMenuOverlayRef.hasAttached()) {
      this.#mobileMenuOverlayRef.attach(new TemplatePortal(this.mobileMenuTpl(), this.#viewContainerRef));
    }

    this.isMobileMenuOpen.set(true);
  }

  #createMobileMenuOverlay(): OverlayRef {
    const overlayRef = this.#overlay.create({
      hasBackdrop: true,
      backdropClass: '',
      positionStrategy: this.#overlay.position().global().top('40px').right('16px'),
      scrollStrategy: this.#overlay.scrollStrategies.block(),
    });

    overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.closeMobileMenu());
    overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          this.closeMobileMenu();
        }
      });
    overlayRef
      .detachments()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.isMobileMenuOpen.set(false));

    return overlayRef;
  }
}
